import { get, keys, union, isNumber } from 'lodash';
import { createSelector } from 'reselect';
import { getNestedKeys, isString, isObject } from 'utils/helpers';

const getCatalogDataObj = (state) => state.catalogs.tables.dataObj;
const getCatalogName = (state, catalogName) => catalogName || state.catalogs.tables.tableName;
export const getCatalogFieldTypesUndefined = (state) => state.catalogs.fieldTypes.undefinedList;
export const getCatalogFieldTypesAll = (state) => state.catalogs.fieldTypes.all;
export const getCatalogFieldTypesIsLoading = (state) =>
  get(state, 'catalogs.fieldTypes.isLoading', false);

export const selectBreadcrumbsForFieldTypes = createSelector(getCatalogName, (catalogName) => ({
  content: `${catalogName}`,
  href: `/catalogs/table/${catalogName}`,
}));

export const selectSampleData = createSelector(
  [getCatalogDataObj, getCatalogName],
  (catalogs, catalogName) => {
    const catalog = get(catalogs, catalogName, {});
    const collection = keys(catalog).map((catalogKey) =>
      typeof catalog[catalogKey].value === 'string'
        ? JSON.parse(catalog[catalogKey].value)
        : catalog[catalogKey].value,
    );
    const samples = collection.reduce((acc, item) => {
      const newItem = keys(item).reduce(
        (itemAccumulator, key) => ({
          ...itemAccumulator,
          [key]: acc[key] ? union([...acc[key], item[key]]) : [item[key]],
        }),
        {},
      );
      return { ...acc, ...newItem };
    }, {});
    /**
     *  samples = {
     *    fieldName1: ['sample 1', 'sample 2', 'sample 3'],
     *    fieldName2: [...],
     *    ...
     *  }
     */
    return samples;
  },
);

export const selectCatalogFieldTypes = createSelector(
  [getCatalogFieldTypesAll, getCatalogName],
  (all, catalogName) => all[catalogName],
);

export const selectCatalogFieldTypesKeys = createSelector([selectCatalogFieldTypes], (all) =>
  keys(all),
);

export const getNestedHumanReadableField = (field) => {
  const fieldNames = field.split('.');
  const name = fieldNames.pop();
  // TODO not sure if should be styling inline here?
  return fieldNames.length > 0 ? (
    <div style={{ paddingLeft: fieldNames.length * 15 }}>{name}</div>
  ) : (
    name
  );
};

export const selectFieldTypeTableRows = createSelector(
  [selectCatalogFieldTypes, selectCatalogFieldTypesKeys, selectSampleData],
  (all, fields, samples) =>
    getNestedKeys(all, true).map((field) => {
      const subKeys = field.split('.');
      let sample1 = null;
      let sample2 = null;

      if (subKeys.length > 1) {
        const rootKey = subKeys[0];
        const sample1Nested = get(samples, [rootKey, '0'], {});
        const sample2Nested = get(samples, [rootKey, '1'], {});
        sample1 = get(sample1Nested, subKeys.slice(1));
        sample2 = get(sample2Nested, subKeys.slice(1));
      } else {
        sample1 = get(samples, [field, '0']);
        sample2 = get(samples, [field, '1']);
      }

      sample1 = !isString(sample1) && !isNumber(sample1) ? JSON.stringify(sample1) : sample1;
      sample2 = !isString(sample2) && !isNumber(sample2) ? JSON.stringify(sample2) : sample2;

      const fieldType = get(all, subKeys);

      return {
        fieldName: getNestedHumanReadableField(field),
        fieldType: isObject(fieldType) ? 'object' : fieldType,
        sample1: isObject(fieldType) && !sample1 ? '{}' : sample1,
        sample2: isObject(fieldType) && !sample2 ? '{}' : sample2,
        fieldNameDotNotation: field,
      };
    }),
);

export const selectDefinedFieldTypes = createSelector([selectCatalogFieldTypes], (all) =>
  getNestedKeys(all, true, true).reduce((acc, fieldMapping) => {
    const k = Object.keys(fieldMapping)[0];
    const fm = typeof fieldMapping[k] === 'object' ? { [k]: 'object' } : fieldMapping;
    return fieldMapping[k] === 'undefined' ? { ...acc } : { ...acc, ...fm };
  }, {}),
);
